import type { LineAuth } from "./liff";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

export type OrderStatus =
  | "รับผ้าแล้ว"
  | "กำลังดำเนินการ"
  | "พร้อมรับ"
  | "รับไปแล้ว";

export type PaymentStatus =
  | "ยังไม่ตั้งยอด"
  | "รอชำระเงิน"
  | "รอตรวจสอบ"
  | "ชำระแล้ว";

export interface PaymentPayload {
  amountBaht: number | null;
  status: PaymentStatus;
  notifiedAt: string | null;
  verifiedAt: string | null;
  updatedAt: string | null;
}

export interface ServiceLinePayload {
  label: string;
  amountBaht: number;
}

export interface ServicePayload {
  washSizeKg: number | null;
  washTemperature: string | null;
  dryMinutes: number | null;
  dryHeat: string | null;
  lines: ServiceLinePayload[];
}

export interface OrderPayload {
  orderId: string;
  status: OrderStatus;
  updatedAt: string;
  service: ServicePayload;
  payment: PaymentPayload;
}

export interface StatusResponse {
  bound: boolean;
  order?: OrderPayload;
  message?: string;
}

export interface BindResponse {
  ok: boolean;
  order?: OrderPayload;
  error?: string;
}

export interface CreateOrderRequest {
  customerName: string;
  phone: string;
  washSizeKg: number | null;
  washTemperature: string | null;
  dryHeat: string | null;
  note?: string;
}

export interface CreateOrderResponse {
  ok: boolean;
  order?: OrderPayload;
  error?: string;
}

export interface PaymentNoticeResponse {
  ok: boolean;
  order?: OrderPayload;
  error?: string;
}

function authHeaders(auth: LineAuth): Record<string, string> {
  if (auth.idToken) {
    return { Authorization: `Bearer ${auth.idToken}` };
  }
  return { "X-Dev-User-Id": auth.lineUserId };
}

async function request<T>(
  path: string,
  auth: LineAuth,
  init?: RequestInit,
): Promise<T> {
  const url = `${API_BASE.replace(/\/$/, "")}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(auth),
      ...init?.headers,
    },
  });
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    throw new Error(
      (data as { error?: string }).error ?? `HTTP ${res.status}`,
    );
  }
  return data;
}

export function fetchStatus(
  auth: LineAuth,
  orderId?: string | null,
): Promise<StatusResponse> {
  const q = new URLSearchParams({ action: "status" });
  if (orderId) q.set("orderId", orderId);
  return request<StatusResponse>(`?${q}`, auth);
}

export function bindPhone(
  auth: LineAuth,
  phone: string,
): Promise<BindResponse> {
  return request<BindResponse>("?action=bind", auth, {
    method: "POST",
    body: JSON.stringify({ phone }),
  });
}

export function createOrder(
  auth: LineAuth,
  body: CreateOrderRequest,
): Promise<CreateOrderResponse> {
  return request<CreateOrderResponse>("?action=create", auth, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function sendPaymentNotice(
  auth: LineAuth,
  orderId?: string | null,
): Promise<PaymentNoticeResponse> {
  return request<PaymentNoticeResponse>("?action=payment-notice", auth, {
    method: "POST",
    body: JSON.stringify({ orderId: orderId ?? null }),
  });
}
